import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import type { PublicBootstrapConfig } from '@cnapse/shared/bootstrap.schema.js';
import { loadAndApplyPublicBootstrap } from '../services/bootstrap.service.js';
import { AppButton } from '../components/design-system';

interface BootstrapContextValue {
  config: PublicBootstrapConfig;
}

const BootstrapContext = createContext<BootstrapContextValue | null>(null);

interface BootstrapProviderProps {
  children: ReactNode;
}

export const BootstrapProvider: React.FC<BootstrapProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<PublicBootstrapConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    loadAndApplyPublicBootstrap()
      .then((loaded) => {
        if (!cancelled) {
          setConfig(loaded);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load application configuration';
          setError(message);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [retryCount]);

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h6" color="error">
          Unable to start application
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {error}
        </Typography>
        <AppButton variant="contained" onClick={() => setRetryCount((count) => count + 1)}>
          Retry
        </AppButton>
      </Box>
    );
  }

  if (!config) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading configuration…
        </Typography>
      </Box>
    );
  }

  return (
    <BootstrapContext.Provider value={{ config }}>
      {children}
    </BootstrapContext.Provider>
  );
};

export function useBootstrap(): BootstrapContextValue {
  const context = useContext(BootstrapContext);
  if (!context) {
    throw new Error('useBootstrap must be used within BootstrapProvider');
  }
  return context;
}
